import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { userService } from "@/services/userService";

export interface UserStatusState {
  isActive: boolean;
  userId: string;
  email: string;
  displayName: string;
  lastLoginAt: string;
  theme: "light" | "dark";
  agencies: string[];
  roles: string[];
  isLoading: boolean;
  error: string | null;
  isNoAgenciesError: boolean;
}

const initialState: UserStatusState = {
  isActive: false,
  userId: "",
  email: "",
  displayName: "",
  lastLoginAt: "",
  theme: "light",
  agencies: [],
  roles: [],
  isLoading: false,
  error: null,
  isNoAgenciesError: false,
};

/**
 * Async thunk to fetch user status
 */
export const fetchUserStatus = createAsyncThunk(
  "userStatus/fetchUserStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await userService.getUserStatus();
      return response;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch user status"
      );
    }
  }
);

/**
 * Async thunk to update theme
 */
export const updateUserTheme = createAsyncThunk(
  "userStatus/updateTheme",
  async (theme: "light" | "dark", { rejectWithValue }) => {
    try {
      await userService.updateTheme(theme);
      return theme;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update theme"
      );
    }
  }
);

const userStatusSlice = createSlice({
  name: "userStatus",
  initialState,
  reducers: {
    /**
     * Clear user status (useful for logout)
     */
    clearUserStatus: (state) => {
      Object.assign(state, initialState);
    },
    /**
     * Update theme locally (optimistic update)
     */
    setTheme: (state, action: PayloadAction<"light" | "dark">) => {
      state.theme = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch user status
      .addCase(fetchUserStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isActive = action.payload.data.isActive;
        state.userId = action.payload.data.userId;
        state.email = action.payload.data.email;
        state.displayName = action.payload.data.displayName;
        state.lastLoginAt = action.payload.data.lastLoginAt;
        state.theme = action.payload.data.theme;
        state.agencies = action.payload.data.agencies || [];
        state.roles = action.payload.data.roles || [];
        state.isNoAgenciesError = action.payload.isNoAgenciesError || false;
        state.error = null;
      })
      .addCase(fetchUserStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update theme
      .addCase(updateUserTheme.fulfilled, (state, action) => {
        state.theme = action.payload;
      });
  },
});

export const { clearUserStatus, setTheme } = userStatusSlice.actions;
export default userStatusSlice.reducer;
